/**
 * Client-Side MOU Access Control Utility
 * Handles recruiter MOU checks and nurse profile filtering without Deno functions
 */

import { lumi } from "./lumi"

export type MOUStatus = "unsigned" | "signed" | "suspended" | "expired"
export type AccessLevel = "limited" | "full"
export type VisibilityMode = "public" | "selective" | "private"

export interface RecruiterMOU {
  _id: string
  userId: string
  mouStatus: MOUStatus
  mouSignedAt: string | null
  mouExpiresAt: string | null
  mouDocumentUrl: string | null
  mouApprovedBy: string | null
  suspensionReason: string | null
  companyName: string
  companyWebsite: string
  contactEmail: string
  contactPhone: string
  createdAt: string
  updatedAt: string
}

export interface NurseVisibilitySettings {
  _id: string
  userId: string
  visibilityMode: VisibilityMode
  allowLimitedProfileAccess: boolean
  selectiveRecruiterWhitelist: string[]
  allowContactInfo: boolean
  allowDocumentDownload: boolean
  allowMessaging: boolean
  allowSalaryView: boolean
  createdAt: string
  updatedAt: string
}

export interface AccessCheckResult {
  accessLevel: AccessLevel
  mouStatus: MOUStatus
  canViewProfile: boolean
  warning: string | null
  expiryDays: number | null
}

/**
 * Check recruiter's MOU status and determine access level
 */
export async function checkRecruiterAccess(recruiterId: string): Promise<AccessCheckResult> {
  try {
    // Fetch recruiter MOU record
    const { list } = await lumi.entities.RecruiterMOU.list({
      filter: { userId: recruiterId }
    })

    if (list.length === 0) {
      // No MOU record found - default to unsigned
      return {
        accessLevel: "limited",
        mouStatus: "unsigned",
        canViewProfile: true,
        warning: "Please sign the MOU to access full nurse profiles",
        expiryDays: null
      }
    }

    const mou = list[0] as unknown as RecruiterMOU

    // Check expiry
    if (mou.mouExpiresAt) {
      const expiryDate = new Date(mou.mouExpiresAt)
      const today = new Date()
      const daysLeft = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

      if (daysLeft < 0 && mou.mouStatus === "signed") {
        // MOU expired
        return {
          accessLevel: "limited",
          mouStatus: "expired",
          canViewProfile: true,
          warning: "Your MOU has expired. Please renew to access full profiles.",
          expiryDays: daysLeft
        }
      }

      if (daysLeft <= 30 && mou.mouStatus === "signed") {
        // Expiring soon warning
        return {
          accessLevel: "full",
          mouStatus: "signed",
          canViewProfile: true,
          warning: `Your MOU expires in ${daysLeft} days. Please renew soon.`,
          expiryDays: daysLeft
        }
      }
    }

    // Check status
    switch (mou.mouStatus) {
      case "signed":
        return {
          accessLevel: "full",
          mouStatus: "signed",
          canViewProfile: true,
          warning: null,
          expiryDays: null
        }
      case "suspended":
        return {
          accessLevel: "limited",
          mouStatus: "suspended",
          canViewProfile: true,
          warning: `Your account is suspended. Reason: ${mou.suspensionReason || "Contact admin"}`,
          expiryDays: null
        }
      case "expired":
        return {
          accessLevel: "limited",
          mouStatus: "expired",
          canViewProfile: true,
          warning: "Your MOU has expired. Please renew to access full profiles.",
          expiryDays: null
        }
      default:
        return {
          accessLevel: "limited",
          mouStatus: "unsigned",
          canViewProfile: true,
          warning: "Please sign the MOU to access full nurse profiles",
          expiryDays: null
        }
    }
  } catch (error) {
    console.error("Error checking recruiter access:", error)
    return {
      accessLevel: "limited",
      mouStatus: "unsigned",
      canViewProfile: true,
      warning: "Unable to verify MOU status",
      expiryDays: null
    }
  }
}

/**
 * Filter nurse profile data based on MOU access level and nurse visibility settings
 */
export async function filterNurseProfile(
  nurseProfile: any,
  recruiterId: string,
  accessLevel: AccessLevel
): Promise<any> {
  // STEP 1: Apply MOU-level filtering
  if (accessLevel === "limited") {
    // Return only limited data
    return {
      _id: nurseProfile._id,
      fullName: nurseProfile.fullName || "Anonymous Nurse",
      country: nurseProfile.countryOfResidence || nurseProfile.nationality || "N/A",
      specialization: nurseProfile.specialties?.slice(0, 2).join(", ") || "N/A",
      educationLevel: nurseProfile.educationLevels?.[0] || "N/A",
      yearsExperience: nurseProfile.yearsExperience || 0,
      isLimitedView: true,
      message: "Sign MOU to view full profile"
    }
  }

  // STEP 2: Recruiter has full MOU access - check nurse visibility settings
  try {
    const { list } = await lumi.entities.NurseVisibilitySettings.list({
      filter: { userId: nurseProfile.userId }
    })

    if (list.length === 0) {
      // No visibility settings - default to public
      return { ...nurseProfile, isLimitedView: false }
    }

    const settings = list[0] as unknown as NurseVisibilitySettings

    // Check visibility mode
    if (settings.visibilityMode === "private") {
      return {
        _id: nurseProfile._id,
        message: "This nurse profile is private",
        isPrivate: true
      }
    }

    if (settings.visibilityMode === "selective") {
      // Check if recruiter is in whitelist
      if (!settings.selectiveRecruiterWhitelist.includes(recruiterId)) {
        return {
          _id: nurseProfile._id,
          fullName: nurseProfile.fullName || "Anonymous Nurse",
          country: nurseProfile.countryOfResidence || nurseProfile.nationality || "N/A",
          specialization: nurseProfile.specialties?.slice(0, 2).join(", ") || "N/A",
          educationLevel: nurseProfile.educationLevels?.[0] || "N/A",
          yearsExperience: nurseProfile.yearsExperience || 0,
          isLimitedView: true,
          message: "You don't have access to this profile"
        }
      }
    }

    // Public or whitelisted - return full profile with settings applied
    const filteredProfile = { ...nurseProfile, isLimitedView: false }

    if (!settings.allowContactInfo) {
      delete filteredProfile.email
      delete filteredProfile.phoneNumber
      delete filteredProfile.whatsappNumber
    }

    if (!settings.allowDocumentDownload) {
      delete filteredProfile.documents
      delete filteredProfile.certificates
    }

    return filteredProfile
  } catch (error) {
    console.error("Error fetching visibility settings:", error)
    // Default to full access if error
    return { ...nurseProfile, isLimitedView: false }
  }
}

/**
 * Log profile view for audit trail
 */
export async function logProfileView(
  nurseUserId: string,
  recruiterUserId: string,
  accessLevel: AccessLevel
): Promise<void> {
  try {
    const recruiterProfile = await lumi.entities.UserProfiles.list({
      filter: { userId: recruiterUserId }
    })

    await lumi.entities.ProfileViewAuditLog.create({
      nurseUserId,
      recruiterUserId,
      recruiterName: recruiterProfile.list[0]?.fullName || "Unknown",
      recruiterCompany: recruiterProfile.list[0]?.companyName || "Unknown",
      dataAccessLevel: accessLevel,
      ipAddress: "N/A", // Client-side can't get real IP
      userAgent: navigator.userAgent,
      viewedAt: new Date().toISOString(),
      sessionId: `${recruiterUserId}-${Date.now()}`
    })
  } catch (error) {
    console.error("Error logging profile view:", error)
  }
}

/**
 * Get nurse's recent profile views (for nurse dashboard)
 */
export async function getNurseProfileViews(nurseUserId: string, limit = 30): Promise<any[]> {
  try {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

    const { list } = await lumi.entities.ProfileViewAuditLog.list({
      filter: {
        nurseUserId,
        viewedAt: { $gte: thirtyDaysAgo.toISOString() }
      },
      sort: { viewedAt: -1 },
      limit
    })

    return list
  } catch (error) {
    console.error("Error fetching profile views:", error)
    return []
  }
}
